/**
 * Editor for a ticketing item's modifiers (`items_ticketing.modifiers`).
 *
 * Each modifier is one choice the customer makes (Hotel, Time), shown as a
 * dropdown on the public page. Each
 * option can add cents to the ticket price. Every combination of options
 * becomes its own token name at sale time, so the editor shows the count
 * and the first few names the ticket will mint. Serialize with
 * `JSON.stringify(groups)` into a hidden `modifiers_json` input.
 */
import { Fragment } from "react";
import { PlusIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { Input } from "../../../../components/inputs/Input";
import PriceInput from "../../../../components/inputs/PriceInput";
import { SecondaryButton, TertiaryButton } from "../../../../components/Button";
import {
  TicketModifierGroup,
  buildVariantName,
  permutationCount,
  permutations,
} from "../../../tokens/variantName";

interface TicketModifiersEditorProps {
  groups: TicketModifierGroup[];
  onChange: (groups: TicketModifierGroup[]) => void;
  /** The ticket name, used for the token name preview. */
  ticketName: string;
  disabled?: boolean;
}

const PREVIEW_LIMIT = 6;
const WARN_AT = 60;

export const TicketModifiersEditor: React.FC<TicketModifiersEditorProps> = ({
  groups,
  onChange,
  ticketName,
  disabled = false,
}) => {
  const update = (fn: (draft: TicketModifierGroup[]) => void) => {
    const draft = structuredClone(groups);
    fn(draft);
    onChange(draft);
  };

  const count = permutationCount(groups);
  const preview = groups.length
    ? permutations(groups)
        .slice(0, PREVIEW_LIMIT)
        .map((labels) => buildVariantName(ticketName || "Ticket", labels))
    : [];

  return (
    <div className="col-span-6 border border-gray-200 rounded-lg p-4 bg-gray-50">
      <div className="flex items-center justify-between mb-1">
        <h4 className="font-semibold text-gray-800">Ticket Modifiers</h4>
        {!disabled && (
          <SecondaryButton
            type="button"
            onClick={() =>
              update((d) => {
                d.push({ name: "", type: "single", required: true, options: [{ label: "", price: 0 }] });
              })
            }
          >
            <PlusIcon className="h-4 w-4 inline mr-1" />
            Add Modifier
          </SecondaryButton>
        )}
      </div>
      <p className="text-sm text-gray-600 mb-3">
        Each modifier is one choice the customer makes for this ticket, shown as a dropdown on
        the public page, for example Hotel or Time. Every combination scans as its own ticket type, and gates can accept the base
        ticket or one exact combination.
      </p>

      {groups.length === 0 && (
        <div className="text-sm text-gray-500 py-2">No modifiers. This ticket sells as is.</div>
      )}

      {groups.map((group, gi) => (
        <div key={gi} className="border border-gray-200 rounded-md bg-white p-3 mb-3">
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Input
                label={`Modifier ${gi + 1} name`}
                name={`ticket_modifier_group_${gi}`}
                value={group.name}
                placeholder="Hotel"
                maxLength={40}
                disabled={disabled}
                onChange={(v) => update((d) => { d[gi].name = String(v ?? ""); })}
              />
            </div>
            {!disabled && (
              <button
                type="button"
                className="text-gray-400 hover:text-red-600 mb-2"
                aria-label="Remove modifier"
                onClick={() => update((d) => { d.splice(gi, 1); })}
              >
                <XCircleIcon className="h-6 w-6" />
              </button>
            )}
          </div>

          <div className="mt-2 grid grid-cols-12 gap-2 items-end">
            {group.options.map((opt, oi) => (
              <Fragment key={oi}>
                <div className="col-span-7">
                  <Input
                    label={`Option ${oi + 1}`}
                    name={`ticket_modifier_${gi}_option_${oi}`}
                    value={opt.label}
                    placeholder="Hilton Garden Inn"
                    maxLength={60}
                    disabled={disabled}
                    onChange={(v) => update((d) => { d[gi].options[oi].label = String(v ?? ""); })}
                  />
                </div>
                <div className="col-span-4">
                  <PriceInput
                    label="Adds to price"
                    name={`ticket_modifier_${gi}_price_${oi}`}
                    value={opt.price / 100}
                    onChange={(v) => update((d) => { d[gi].options[oi].price = Math.round((v || 0) * 100); })}
                  />
                </div>
                <div className="col-span-1 flex justify-center pb-2">
                  {!disabled && (
                    <button
                      type="button"
                      className="text-gray-400 hover:text-red-600"
                      aria-label="Remove option"
                      onClick={() => update((d) => { d[gi].options.splice(oi, 1); })}
                    >
                      <XCircleIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>
              </Fragment>
            ))}
          </div>
          {!disabled && (
            <div className="mt-2">
              <TertiaryButton
                type="button"
                onClick={() => update((d) => { d[gi].options.push({ label: "", price: 0 }); })}
              >
                + Add option
              </TertiaryButton>
            </div>
          )}
        </div>
      ))}

      {groups.length > 0 && (
        <div className="text-sm text-gray-700">
          <div>
            This ticket will mint <b>{count}</b> ticket type{count === 1 ? "" : "s"}
            {count > WARN_AT && (
              <span className="text-amber-700"> (a large list for the gate picker)</span>
            )}
            .
          </div>
          <ul className="mt-1 text-xs text-gray-500 font-mono">
            {preview.map((name) => (
              <li key={name}>{name}</li>
            ))}
            {count > PREVIEW_LIMIT && <li>… and {count - PREVIEW_LIMIT} more</li>}
          </ul>
        </div>
      )}
    </div>
  );
};

export default TicketModifiersEditor;
